// API routes serialize Postgres `timestamp` columns without a timezone suffix
// (e.g. "2026-07-05T12:05:22.513"). Those values are UTC, but `new Date(...)`
// treats suffix-less strings as LOCAL time, skewing every relative-time display
// by the user's UTC offset. Always parse server timestamps through here.

const HAS_TZ_SUFFIX = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

export function parseServerDate(iso: string): Date {
  // Date-only strings ("2026-07-05") are already parsed as UTC by spec —
  // appending 'Z' to them would produce an Invalid Date.
  if (iso.includes('T') && !HAS_TZ_SUFFIX.test(iso)) return new Date(iso + 'Z');
  return new Date(iso);
}

/**
 * Publish timestamp in the viewer's local time: "6 Jul, 03:37" — the year is
 * added when it isn't the current one (or with alwaysYear). Mirrors the mobile
 * app's formatDateTime (en-GB for day-first order, 24h clock).
 */
export function formatDateTime(dateStr: string, opts?: { alwaysYear?: boolean }): string {
  const d = parseServerDate(dateStr);
  const withYear = opts?.alwaysYear || d.getFullYear() !== new Date().getFullYear();
  const date = d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
  });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${date}, ${time}`;
}

/** Relative time with suffix: "5m ago", "3h ago", "2d ago". */
export function timeAgo(dateStr: string): string {
  const diff = Math.max(0, Date.now() - parseServerDate(dateStr).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/** Compact relative time: "42s", "5m", "3h", "2d". */
export function timeAgoShort(dateStr: string): string {
  const diff = Math.max(0, Math.floor((Date.now() - parseServerDate(dateStr).getTime()) / 1000));
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
